/**
 * Task #1829 — Tab "Catalogo" MCP preset ufficiali verificati vendor.
 *
 * Griglia card con logo, descrizione, badge, CTA "Connetti". Click → apre
 * PresetConnectModal (form dinamico data-driven dai custom_fields del preset).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient as api } from '@/lib/api';
import PresetConnectModal, { PresetLogo, type CatalogPreset } from './PresetConnectModal';

export default function CatalogTab() {
  const [selected, setSelected] = useState<CatalogPreset | null>(null);

  const { data, isLoading } = useQuery<CatalogPreset[]>({
    queryKey: ['mcp-catalog-presets'],
    queryFn: async () => {
      const res = await api.get<CatalogPreset[]>('/mcp/catalog/presets');
      return res.data ?? [];
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  const presets = data ?? [];
  if (presets.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Nessun preset disponibile.</p>;
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {presets.map((p) => (
          <Card key={p.slug} className="relative transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <PresetLogo slug={p.slug} name={p.name} accentColor={p.accent_color} />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-base">{p.name}</CardTitle>
                  <div className="flex flex-wrap items-center gap-1 mt-0.5">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.category_label}</Badge>
                    {p.badge && (
                      <span title={`Verificato: ${p.badge}`}><ShieldCheck className="h-3 w-3 text-emerald-500" /></span>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-4">{p.description_it}</p>
              <div className="flex gap-2">
                {p.configured || p.credential_mode === 'tenant_byo' ? (
                  <Button size="sm" className="flex-1" onClick={() => setSelected(p)}>
                    {p.credential_mode === 'tenant_byo' && !p.has_tenant_credentials ? 'Configura e connetti' : 'Connetti'}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="flex-1" disabled title="Credenziali OAuth non configurate">
                    Configurazione richiesta
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <PresetConnectModal preset={selected} open={!!selected} onClose={() => setSelected(null)} />
    </>
  );
}
